import cn from "classnames";
import { Field } from "components/shared/form";
import Select from "components/shared/select";
import Toggle from "components/shared/toggle";
import { serializeReq, validateForm } from "helpers/form";
import { requiredField } from "helpers/validators";
import React, { useCallback, useEffect, useState } from "react";
import { applyBankAccount, deleteBankAccount, getBankAccounts } from "sources/books/legalPersons";
import { getBanks } from "sources/profile/organization";

const newAccountKey = "new";

const emptyAccount = {
    id: 0,
    name: "",
    bankId: "",
    bankName: "",
    settlementAccount: "",
    correspondentAccount: "",
    isDefault: false,
};

const config = {
    name: {
        label: "Название",
        inputProps: { name: "name", className: "form-control form-control-xs" },
        validators: [requiredField],
    },
    bankId: {
        label: "БИК",
        inputProps: { name: "bankId", className: "form-control form-control-xs" },
        validators: [requiredField],
        url: getBanks,
        propName: (item) => [item.bankId, item.bank].filter(Boolean).join(" - "),
        idKey: "bankId",
        minimumInputLength: 3,
        menuPortalTarget: document.body,
    },
    bankName: {
        label: "Банк",
        inputProps: { name: "bankName", className: "form-control form-control-xs" },
        validators: [requiredField],
    },
    settlementAccount: {
        label: "Расчетный счет",
        inputProps: { name: "settlementAccount", className: "form-control form-control-xs" },
        validators: [requiredField],
    },
    correspondentAccount: {
        label: "Корреспондентский счет",
        inputProps: { name: "correspondentAccount", className: "form-control form-control-xs" },
        validators: [requiredField],
    },
};

const fieldProps = {
    bWrapper: "mb-0",
    fieldWrapper: "field",
};

export default function BankAccounts({ legalPersonId, modal }) {
    const [accounts, setAccounts] = useState([]);
    const [draft, setDraft] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [valid, setValid] = useState(true);
    const [savingId, setSavingId] = useState(null);

    const loadAccounts = useCallback(() => {
        setLoading(true);
        return getBankAccounts(legalPersonId)
            .then((data) => setAccounts(data || []))
            .catch(() => undefined)
            .finally(() => setLoading(false));
    }, [legalPersonId]);

    useEffect(() => {
        loadAccounts();
    }, [loadAccounts]);

    const startCreate = () => {
        setEditingId(newAccountKey);
        setDraft({ ...emptyAccount, isDefault: accounts.length === 0 });
    };

    const startEdit = (account) => {
        setEditingId(account.id);
        setDraft({ ...emptyAccount, ...account });
    };

    const cancelEdit = () => {
        setEditingId(null);
        setDraft(null);
    };

    const onFieldChange = ({ name, value }) => setDraft({ ...draft, [name]: value });

    const saveAccount = () => {
        if (!validateForm(config, serializeReq(draft, config))) {
            setValid(false);
            return;
        }
        setValid(true);

        const rowKey = editingId;
        setSavingId(rowKey);
        applyBankAccount({
            ...draft,
            id: draft.id || 0,
            legalPersonId: Number(legalPersonId),
            isDefault: !!draft.isDefault,
        })
            .then(() => {
                cancelEdit();
                return loadAccounts();
            })
            .catch(() => undefined)
            .finally(() => setSavingId(null));
    };

    const removeAccount = (account) => {
        modal({
            name: "confirm",
            title: "Удаление банковского счета",
            text: "Удалить банковский счет? После удаления его нельзя будет выбрать в новых заявках.",
            action: () => {
                setSavingId(account.id);
                deleteBankAccount({ id: account.id, legalPersonId: Number(legalPersonId) })
                    .then(loadAccounts)
                    .catch(() => undefined)
                    .finally(() => setSavingId(null));
            },
            buttons: { confirm: "Удалить", cancel: "Отмена" },
        });
    };

    const handleBankChange = ({ value, option }) => {
        const data = option
            ? {
                  ...draft,
                  bankName: option.displayName || "",
                  bankId: option.bic || value || "",
              }
            : {
                  ...draft,
                  bankName: "",
                  bankId: value || "",
              };
        setDraft(data);
    };

    const renderField = (name) => (
        <Field {...config[name]} {...fieldProps} label="" value={draft?.[name]} onChange={onFieldChange} />
    );

    const renderBankSelect = () => (
        <Select
            {...config.bankId}
            {...fieldProps}
            label=""
            value={draft?.bankId}
            onChange={handleBankChange}
            styles={{
                control: (styles) => ({ ...styles, minHeight: "22px", fontSize: "12px" }),
                input: (styles) => ({ ...styles, height: "15px", paddingTop: 0, marginTop: 0 }),
                singleValue: (styles) => ({ ...styles, height: "18px", lineHeight: "18px", textAlign: "left" }),
                valueContainer: (styles) => ({ ...styles, lineHeight: "18px", padding: "0 2px" }),
                indicatorsContainer: (styles) => ({ ...styles, height: "21px" }),
                clearIndicator: (styles) => ({ ...styles, padding: "0" }),
                dropdownIndicator: (styles) => ({ ...styles, padding: "0" }),
                option: (styles) => ({ ...styles, fontSize: "12px" }),
                placeholder: (styles) => ({
                    ...styles,
                    lineHeight: "18px",
                    maxWidth: "calc(100% - 16px)",
                    whiteSpace: "nowrap",
                }),
                menu: (styles) => ({ ...styles, zIndex: 11, width: "200px" }),
            }}
        />
    );

    const renderEditRow = (rowKey) => (
        <tr
            key={rowKey}
            className={cn("bg-light", { loading: savingId === rowKey, "book-form__body--invalid": !valid })}
        >
            <td>{renderField("name")}</td>
            <td>{renderField("bankName")}</td>
            <td>{renderBankSelect()}</td>
            <td>{renderField("settlementAccount")}</td>
            <td>{renderField("correspondentAccount")}</td>
            <td>
                <Toggle value={!!draft?.isDefault} onChange={(value) => setDraft({ ...draft, isDefault: value })} />
            </td>
            <td className="text-nowrap">
                <button
                    className="btn btn-xs btn-secondary mr-2"
                    type="button"
                    onClick={cancelEdit}
                    disabled={savingId !== null}
                >
                    Отмена
                </button>
                <button
                    className="btn btn-xs btn-brand"
                    type="button"
                    onClick={saveAccount}
                    disabled={savingId !== null}
                >
                    Сохранить
                </button>
            </td>
        </tr>
    );

    const renderAccountRow = (account) => {
        if (editingId === account.id) {
            return renderEditRow(account.id);
        }

        return (
            <tr key={account.id} className={cn({ loading: savingId === account.id })}>
                <td>{account.name || "-"}</td>
                <td>{account.bankName || "-"}</td>
                <td>{account.bankId || "-"}</td>
                <td>{account.settlementAccount || "-"}</td>
                <td>{account.correspondentAccount || "-"}</td>
                <td>{account.isDefault && <span className="badge badge-primary">Основной</span>}</td>
                <td className="text-nowrap">
                    <button
                        className="btn btn-xs btn-secondary mr-2"
                        type="button"
                        onClick={() => startEdit(account)}
                        disabled={savingId !== null || editingId !== null}
                    >
                        Редактировать
                    </button>
                    <button
                        className="btn btn-xs btn-outline-danger"
                        type="button"
                        onClick={() => removeAccount(account)}
                        disabled={savingId !== null || editingId !== null}
                    >
                        Удалить
                    </button>
                </td>
            </tr>
        );
    };

    const shouldRenderTable = accounts.length > 0 || editingId === newAccountKey;

    return (
        <div className={cn("card card-body mb-3", { loading })}>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Банковские счета</h5>
                {editingId === null && (
                    <button className="btn btn-xs btn-brand" type="button" onClick={startCreate}>
                        Добавить счет
                    </button>
                )}
            </div>
            {!shouldRenderTable && <div className="text-muted">Банковские счета не добавлены</div>}
            {shouldRenderTable && (
                <div className="table-responsive">
                    <table className="table table-sm table-bordered mb-0">
                        <thead>
                            <tr>
                                <th>Название</th>
                                <th>Банк</th>
                                <th style={{ minWidth: "130px" }}>БИК</th>
                                <th>Расчетный счет</th>
                                <th>Корр. счет</th>
                                <th>Основной</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {editingId === newAccountKey && renderEditRow(newAccountKey)}
                            {accounts.map(renderAccountRow)}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
